/**
 * Ingreso con la suite Datalog (GUIA-AGENTES.md §3.1 de la suite): SGDEA no tiene pantalla de ingreso. Sin sesión, la
 * web va directo a `/auth/oidc/start` del API (un spinner mientras tanto), que sigue a la suite: quien ya está en el
 * portal vuelve sin ver nada; quien no, ve la portada de la suite con la cortina de SGDEA abierta y, al terminar,
 * vuelve a la ruta que pidió. Si el ingreso falla, el API manda a la portada de la suite con el código: SGDEA nunca
 * muestra el error ni reintenta sola, así que no hacen falta guardas contra bucles.
 */

export interface SuiteLoginOptions {
  /** Entidad que recuerda este navegador, si alguna: la suite entra a esa. Sin ella, decide la suite. */
  tenant?: string | null;
  /** Ruta de la web a la que volver al terminar (la que la persona pidió sin sesión). */
  volver?: string | null;
}

/**
 * `/auth/oidc/start` con la entidad y a dónde volver. Nunca `prompt=login`: la sesión es la de la suite (fila «La sesión
 * es la de la suite»), así que con sesión en el portal SGDEA vuelve a abrirse sin pedir nada.
 */
export function suiteLoginUrl({ tenant, volver }: SuiteLoginOptions = {}): string {
  const params = new URLSearchParams();
  if (tenant) params.set('tenant', tenant);
  if (volver && volver !== '/' && !volver.startsWith('/login')) params.set('volver', volver);
  const qs = params.toString();
  return `/api/v1/auth/oidc/start${qs ? `?${qs}` : ''}`;
}

/**
 * La pestaña ya va a otra dirección (terminar el SSO al salir, continuar en la otra sesión) o está saliendo («Salir»
 * espera al API): desde ahí ningún 401 la manda a ingresar (`api/client.ts`) y la pantalla de ingreso, que se dibuja
 * mientras el navegador carga esa dirección, no empieza otro ingreso encima.
 */
let leaving = false;
export const isLeaving = () => leaving;
/** «Salir» marca la pestaña antes de pedir al API que cierre, y la desmarca si el API no respondió. */
export const setLeaving = (v: boolean) => { leaving = v; };

/** Lleva la pestaña a `url` (una dirección absoluta de la suite) y la deja marcada como «saliendo». */
export function leaveTo(url: string) {
  leaving = true;
  window.location.assign(url);
}

export function redirectToSuite(opts: SuiteLoginOptions) {
  leaving = true;
  window.location.replace(suiteLoginUrl(opts));
}
